'use client';

import type { Money, ProductVariant } from '@/lib/shopify/types';
import { formatPrice } from '@/lib/shopify';

export interface SizeQuantityRow {
  size: string;
  variant: ProductVariant | null;
  available: boolean;
  quantity: number;
}

export interface SizeQuantityLine {
  variant: ProductVariant;
  quantity: number;
  size: string;
}

interface SizeQuantitySelectorProps {
  rows: SizeQuantityRow[];
  currencyCode: string;
  onQuantityChange: (size: string, quantity: number) => void;
  onClear: () => void;
}

function money(amount: number, currencyCode: string): Money {
  return {
    amount: amount.toFixed(2),
    currencyCode,
  };
}

function openSizeGuide() {
  window.dispatchEvent(new CustomEvent('open-size-guide'));
}

export default function SizeQuantitySelector({
  rows,
  currencyCode,
  onQuantityChange,
  onClear,
}: SizeQuantitySelectorProps) {
  const selectedQuantity = rows.reduce((sum, row) => sum + row.quantity, 0);
  const total = rows.reduce((sum, row) => {
    if (!row.variant || row.quantity <= 0) return sum;
    return sum + parseFloat(row.variant.price.amount) * row.quantity;
  }, 0);

  const setQuantity = (size: string, quantity: number) => {
    onQuantityChange(size, Math.min(999, Math.max(0, quantity)));
  };

  return (
    <div className="border border-[var(--color-border)] bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] px-4 py-3">
        <div>
          <span className="block text-sm font-medium uppercase tracking-wider text-black">Size Quantity</span>
          <span className="mt-1 block text-xs text-[var(--color-text-secondary)]">
            {selectedQuantity > 0 ? `${selectedQuantity} selected` : 'Choose one or more sizes for this design'}
          </span>
        </div>
        <button
          type="button"
          onClick={openSizeGuide}
          className="shrink-0 text-xs text-[var(--color-text-secondary)] underline hover:text-black"
        >
          Size Guide
        </button>
      </div>

      <div className="divide-y divide-[var(--color-border)]">
        {rows.map((row) => {
          const disabled = !row.available || !row.variant;
          return (
            <div
              key={row.size}
              className={`grid grid-cols-[minmax(3rem,1fr)_auto] items-center gap-3 px-4 py-3 ${
                disabled ? 'bg-gray-50 text-gray-400' : 'text-black'
              }`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{row.size}</span>
                  {disabled && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      Out of stock
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-[2.25rem_3.25rem_2.25rem]">
                <button
                  type="button"
                  disabled={disabled || row.quantity <= 0}
                  onClick={() => setQuantity(row.size, row.quantity - 1)}
                  className="flex h-9 items-center justify-center border border-[var(--color-border)] text-sm hover:border-black disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label={`Decrease ${row.size} quantity`}
                >
                  -
                </button>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  disabled={disabled}
                  value={row.quantity > 0 ? String(row.quantity) : ''}
                  onChange={(event) => {
                    const raw = event.target.value;
                    if (raw === '') {
                      setQuantity(row.size, 0);
                      return;
                    }
                    if (/^\d+$/.test(raw)) setQuantity(row.size, parseInt(raw, 10));
                  }}
                  className="h-9 border-y border-[var(--color-border)] bg-white text-center text-sm font-medium focus:outline-none disabled:bg-gray-50"
                  aria-label={`${row.size} quantity`}
                />
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => setQuantity(row.size, row.quantity + 1)}
                  className="flex h-9 items-center justify-center border border-[var(--color-border)] text-sm hover:border-black disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label={`Increase ${row.size} quantity`}
                >
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-[var(--color-border)] px-4 py-3">
        <div>
          <span className="block text-xs font-semibold uppercase tracking-wider text-black">Total</span>
          <span className="mt-1 block text-sm font-semibold text-black">{formatPrice(money(total, currencyCode))}</span>
        </div>
        <button
          type="button"
          disabled={selectedQuantity === 0}
          onClick={onClear}
          className="border border-[var(--color-border)] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-black hover:border-black disabled:cursor-not-allowed disabled:opacity-40"
        >
          Clear sizes
        </button>
      </div>
    </div>
  );
}
